import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/shared/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 active:scale-95 touch-manipulation",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow hover:bg-primary/90 focus:bg-primary/90 active:bg-primary/95",
        destructive:
          "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90 focus:bg-destructive/90 active:bg-destructive/95",
        outline:
          "border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground active:bg-accent/80",
        secondary:
          "bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80 focus:bg-secondary/80 active:bg-secondary/70",
        ghost: "hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground active:bg-accent/80",
        link: "text-primary underline-offset-4 hover:underline focus:underline active:text-primary/80",
      },
      size: {
        default: "h-touch-sm sm:h-10 px-4 py-2 min-w-touch-sm sm:min-w-[40px] [&_svg]:size-5 sm:[&_svg]:size-4",
        sm: "h-touch-sm sm:h-9 rounded-md px-3 text-xs min-w-touch-sm sm:min-w-[36px] [&_svg]:size-4 sm:[&_svg]:size-3.5",
        lg: "h-touch-md sm:h-11 rounded-md px-8 min-w-touch-md sm:min-w-[44px] [&_svg]:size-6 sm:[&_svg]:size-5",
        icon: "h-touch-sm w-touch-sm sm:h-10 sm:w-10 min-w-touch-sm min-h-touch-sm sm:min-w-[40px] sm:min-h-[40px] [&_svg]:size-5 sm:[&_svg]:size-4",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  isLoading?: boolean
  'aria-busy'?: boolean | 'true' | 'false'
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, isLoading = false, disabled, 'aria-busy': ariaBusy, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    
    // ローディング状態やdisabled状態の処理
    const isDisabled = disabled || isLoading;
    const ariaDisabled = isDisabled ? 'true' : undefined;
    const busyState = ariaBusy || (isLoading ? 'true' : undefined);
    
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        disabled={isDisabled}
        aria-disabled={ariaDisabled}
        aria-busy={busyState}
        role={asChild ? undefined : "button"}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
